import { useCallback, useEffect, useState } from "react";

import { useAtom } from "../app-jotai";
import {
  projectsApi,
  screensApi,
  type Project,
  type ScreenSummary,
} from "../data/api";
import { activeScreenIdAtom, dashboardOpenAtom } from "../data/authState";
import { BrandLogo } from "./BrandLogo";

import "./Dashboard.scss";

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d ago`;
  }
  return new Date(iso).toLocaleDateString();
};

const initial = (name: string) => name.trim().charAt(0).toUpperCase() || "?";

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

type IconProps = { size?: number };

const IconLogOut = ({ size = 15 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

const IconClose = ({ size = 16 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const IconPlusSquare = ({ size = 17 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </svg>
);

const IconFolderPlus = ({ size = 17 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 11v6" />
    <path d="M9 14h6" />
  </svg>
);

const IconPlus = ({ size = 13 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const IconClock = ({ size = 13 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4l3 2" />
  </svg>
);

const IconArrowRight = ({ size = 14 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </svg>
);

const IconPencil = ({ size = 15 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const IconTrash = ({ size = 15 }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
  </svg>
);

type DashboardProps = {
  currentUserEmail: string;
  onOpenScreen: (id: string) => void;
  onCreateScreen: (projectId: string | null, name: string) => void;
  onClose?: () => void;
  onLogout: () => void;
  hasLocalScene: boolean;
  onImportLocalScene: () => void;
};

export const Dashboard = ({
  currentUserEmail,
  onOpenScreen,
  onCreateScreen,
  onClose,
  onLogout,
  hasLocalScene,
  onImportLocalScene,
}: DashboardProps) => {
  const [isOpen] = useAtom(dashboardOpenAtom);
  const activeScreenId = useAtom(activeScreenIdAtom)[0];

  const [projects, setProjects] = useState<Project[]>([]);
  const [screens, setScreens] = useState<ScreenSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  const [creatingScreenFor, setCreatingScreenFor] = useState<
    string | "unfiled" | null
  >(null);
  const [newScreenName, setNewScreenName] = useState("");

  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState("");

  const [editingScreenId, setEditingScreenId] = useState<string | null>(null);
  const [editingScreenName, setEditingScreenName] = useState("");

  const [confirmDeleteProjectId, setConfirmDeleteProjectId] = useState<
    string | null
  >(null);
  const [confirmDeleteScreenId, setConfirmDeleteScreenId] = useState<
    string | null
  >(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [projectList, screenList] = await Promise.all([
        projectsApi.list(),
        screensApi.list(),
      ]);
      setProjects(projectList);
      setScreens(screenList);
    } catch (err) {
      setError("Couldn't load your screens");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      reload();
      setBannerDismissed(false);
    }
  }, [isOpen, reload]);

  if (!isOpen) {
    return null;
  }

  const closeAllEditors = () => {
    setIsCreatingProject(false);
    setNewProjectName("");
    setCreatingScreenFor(null);
    setNewScreenName("");
    setEditingProjectId(null);
    setEditingScreenId(null);
    setConfirmDeleteProjectId(null);
    setConfirmDeleteScreenId(null);
  };

  const submitNewProject = async () => {
    const name = newProjectName.trim();
    if (!name) {
      return;
    }
    await projectsApi.create(name);
    closeAllEditors();
    reload();
  };

  const submitNewScreen = (projectId: string | null) => {
    const name = newScreenName.trim();
    if (!name) {
      return;
    }
    closeAllEditors();
    onCreateScreen(projectId, name);
  };

  const submitRenameProject = async (project: Project) => {
    const name = editingProjectName.trim();
    if (!name || name === project.name) {
      closeAllEditors();
      return;
    }
    await projectsApi.rename(project.id, name);
    closeAllEditors();
    reload();
  };

  const submitRenameScreen = async (screen: ScreenSummary) => {
    const name = editingScreenName.trim();
    if (!name || name === screen.name) {
      closeAllEditors();
      return;
    }
    await screensApi.update(screen.id, { name });
    closeAllEditors();
    reload();
  };

  const deleteProject = async (project: Project) => {
    await projectsApi.remove(project.id);
    closeAllEditors();
    reload();
  };

  const deleteScreen = async (screen: ScreenSummary) => {
    await screensApi.remove(screen.id);
    closeAllEditors();
    reload();
  };

  const renderScreenRow = (screen: ScreenSummary) => {
    if (confirmDeleteScreenId === screen.id) {
      return (
        <tr key={screen.id} className="Dashboard__row">
          <td className="Dashboard__thumbCell">
            <span className="Dashboard__thumb" />
          </td>
          <td colSpan={3}>
            <div className="Dashboard__confirmRow">
              <span>Delete &quot;{screen.name}&quot;?</span>
              <div className="Dashboard__confirmActions">
                <button
                  type="button"
                  className="Dashboard__confirmDelete"
                  onClick={() => deleteScreen(screen)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="Dashboard__linkButton"
                  onClick={closeAllEditors}
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (editingScreenId === screen.id) {
      return (
        <tr key={screen.id} className="Dashboard__row">
          <td className="Dashboard__thumbCell">
            <span className="Dashboard__thumb" />
          </td>
          <td colSpan={3}>
            <input
              className="Dashboard__renameInput"
              autoFocus
              value={editingScreenName}
              onChange={(e) => setEditingScreenName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  submitRenameScreen(screen);
                }
                if (e.key === "Escape") {
                  closeAllEditors();
                }
              }}
              onBlur={() => submitRenameScreen(screen)}
            />
          </td>
        </tr>
      );
    }

    return (
      <tr key={screen.id} className="Dashboard__row">
        <td className="Dashboard__thumbCell">
          <span className="Dashboard__thumb" />
        </td>
        <td className="Dashboard__nameCell">
          {screen.name}
          {screen.id === activeScreenId ? " (current)" : ""}
        </td>
        <td className="Dashboard__updatedCell">
          <span className="Dashboard__updatedBadge">
            <IconClock />
            {timeAgo(screen.updatedAt)}
          </span>
        </td>
        <td className="Dashboard__actionsCell">
          <div className="Dashboard__rowActions">
            <button
              type="button"
              className="Dashboard__openButton"
              onClick={() => onOpenScreen(screen.id)}
            >
              Open
              <IconArrowRight />
            </button>
            <button
              type="button"
              className="Dashboard__iconOnlyButton"
              aria-label="Rename"
              onClick={() => {
                closeAllEditors();
                setEditingScreenId(screen.id);
                setEditingScreenName(screen.name);
              }}
            >
              <IconPencil />
            </button>
            <button
              type="button"
              className="Dashboard__iconOnlyButton Dashboard__iconOnlyButton--danger"
              aria-label="Delete"
              onClick={() => {
                closeAllEditors();
                setConfirmDeleteScreenId(screen.id);
              }}
            >
              <IconTrash />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderNewScreenRow = (projectId: string | null) => {
    const key = projectId ?? "unfiled";
    if (creatingScreenFor !== key) {
      return null;
    }
    return (
      <tr className="Dashboard__row">
        <td className="Dashboard__thumbCell">
          <span className="Dashboard__thumb" />
        </td>
        <td colSpan={3}>
          <input
            className="Dashboard__renameInput"
            autoFocus
            placeholder="Screen name"
            value={newScreenName}
            onChange={(e) => setNewScreenName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                submitNewScreen(projectId);
              }
              if (e.key === "Escape") {
                closeAllEditors();
              }
            }}
            onBlur={() => {
              if (newScreenName.trim()) {
                submitNewScreen(projectId);
              } else {
                closeAllEditors();
              }
            }}
          />
        </td>
      </tr>
    );
  };

  const unfiledScreens = screens.filter((s) => !s.projectId);

  const renderSection = (
    title: string,
    count: number,
    key: string,
    projectId: string | null,
    project: Project | null,
    items: ScreenSummary[],
  ) => (
    <section className="Dashboard__section" key={key}>
      <div className="Dashboard__sectionHeader">
        <span
          className={
            project
              ? "Dashboard__avatar"
              : "Dashboard__avatar Dashboard__avatar--neutral"
          }
          aria-hidden="true"
        >
          {project ? (
            initial(project.name)
          ) : (
            <span className="Dashboard__thumb" />
          )}
        </span>

        <div className="Dashboard__sectionTitleGroup">
          {project && editingProjectId === project.id ? (
            <input
              className="Dashboard__renameInput"
              autoFocus
              value={editingProjectName}
              onChange={(e) => setEditingProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  submitRenameProject(project);
                }
                if (e.key === "Escape") {
                  closeAllEditors();
                }
              }}
              onBlur={() => submitRenameProject(project)}
            />
          ) : (
            <h3 className="Dashboard__sectionTitle">{title}</h3>
          )}
          <span className="Dashboard__sectionCount">
            {pluralize(count, "screen")}
          </span>
        </div>

        <div className="Dashboard__sectionActions">
          {project && confirmDeleteProjectId === project.id ? (
            <>
              <span className="Dashboard__cardMeta">
                Delete this project and its screens?
              </span>
              <button
                type="button"
                className="Dashboard__confirmDelete"
                onClick={() => deleteProject(project)}
              >
                Delete
              </button>
              <button
                type="button"
                className="Dashboard__linkButton"
                onClick={closeAllEditors}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="Dashboard__pillButton"
                onClick={() => {
                  closeAllEditors();
                  setCreatingScreenFor(projectId ?? "unfiled");
                }}
              >
                <IconPlus />
                Screen
              </button>
              {project && (
                <>
                  <button
                    type="button"
                    className="Dashboard__linkButton"
                    onClick={() => {
                      closeAllEditors();
                      setEditingProjectId(project.id);
                      setEditingProjectName(project.name);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="Dashboard__linkButton Dashboard__linkButton--danger"
                    onClick={() => {
                      closeAllEditors();
                      setConfirmDeleteProjectId(project.id);
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {(items.length > 0 || creatingScreenFor === (projectId ?? "unfiled")) && (
        <table className="Dashboard__table">
          <thead>
            <tr>
              <th className="Dashboard__thumbCol" />
              <th>Screen</th>
              <th className="Dashboard__updatedCol">Last updated</th>
              <th className="Dashboard__actionsCol">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(renderScreenRow)}
            {renderNewScreenRow(projectId)}
          </tbody>
        </table>
      )}

      {items.length === 0 && creatingScreenFor !== (projectId ?? "unfiled") && (
        <div className="Dashboard__cardMeta">No screens yet</div>
      )}

      <button
        type="button"
        className="Dashboard__addScreenRow"
        onClick={() => {
          closeAllEditors();
          setCreatingScreenFor(projectId ?? "unfiled");
        }}
      >
        <span className="Dashboard__addScreenIcon">
          <IconPlus size={15} />
        </span>
        <span className="Dashboard__addScreenLabel">
          Add a screen{project ? ` to ${project.name}` : ""}
        </span>
        <span className="Dashboard__addScreenHint">Blank canvas</span>
      </button>
    </section>
  );

  const isEmpty = projects.length === 0 && screens.length === 0;

  return (
    <div className="Dashboard">
      <div className="Dashboard__topbar">
        <div className="Dashboard__brand">
          <BrandLogo />
        </div>
        <div className="Dashboard__topbarActions">
          <button
            type="button"
            className="Dashboard__iconButton"
            onClick={onLogout}
          >
            <IconLogOut />
            Log out
          </button>
          {onClose && (
            <button
              type="button"
              className="Dashboard__iconButton Dashboard__iconButton--square"
              aria-label="Close"
              onClick={onClose}
            >
              <IconClose />
            </button>
          )}
        </div>
      </div>

      <div className="Dashboard__scroll">
        <section className="Dashboard__hero">
          <div className="Dashboard__heroInner">
            <div className="Dashboard__heroMain">
              <div className="Dashboard__heroEyebrow">
                Workspace{currentUserEmail ? ` · ${currentUserEmail}` : ""}
              </div>
              <h1 className="Dashboard__heroTitle">
                Welcome
                <br />
                back.
              </h1>
            </div>
            <div className="Dashboard__heroSide">
              <p className="Dashboard__heroSubtitle">
                Pick a screen to continue, or start something new on a fresh
                canvas.
              </p>
              <div className="Dashboard__heroActions">
                <button
                  type="button"
                  className="Dashboard__heroActionPrimary"
                  onClick={() => {
                    closeAllEditors();
                    setCreatingScreenFor("unfiled");
                  }}
                >
                  <IconPlusSquare />
                  New screen
                </button>
                <button
                  type="button"
                  className="Dashboard__heroActionSecondary"
                  onClick={() => {
                    closeAllEditors();
                    setIsCreatingProject(true);
                  }}
                >
                  <IconFolderPlus />
                  New project
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="Dashboard__body">
          {isCreatingProject && (
            <div className="Dashboard__inlineForm">
              <input
                autoFocus
                placeholder="Project name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    submitNewProject();
                  }
                  if (e.key === "Escape") {
                    closeAllEditors();
                  }
                }}
                onBlur={() => {
                  if (newProjectName.trim()) {
                    submitNewProject();
                  } else {
                    closeAllEditors();
                  }
                }}
              />
            </div>
          )}

          {hasLocalScene && !bannerDismissed && (
            <div className="Dashboard__banner">
              <span>You have an unsaved local drawing on this device.</span>
              <div className="Dashboard__bannerActions">
                <button
                  type="button"
                  className="Dashboard__primaryButton"
                  onClick={onImportLocalScene}
                >
                  Import as a screen
                </button>
                <button
                  type="button"
                  className="Dashboard__linkButton"
                  onClick={() => setBannerDismissed(true)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {error && <div className="Dashboard__empty">{error}</div>}

          {!error && isLoading && (
            <div className="Dashboard__empty">Loading your screens…</div>
          )}

          {!error && !isLoading && isEmpty && creatingScreenFor === null && (
            <div className="Dashboard__empty">
              You don't have any screens yet — create your first one above.
            </div>
          )}

          {!error && !isLoading && (!isEmpty || creatingScreenFor !== null) && (
            <>
              <div className="Dashboard__contentHeader">
                <h4 className="Dashboard__contentTitle">Projects</h4>
                <span className="Dashboard__contentSummary">
                  {pluralize(projects.length, "project")} ·{" "}
                  {pluralize(screens.length, "screen")}
                </span>
              </div>

              <div className="Dashboard__content">
                {(unfiledScreens.length > 0 ||
                  creatingScreenFor === "unfiled") &&
                  renderSection(
                    "Unfiled",
                    unfiledScreens.length,
                    "unfiled",
                    null,
                    null,
                    unfiledScreens,
                  )}
                {projects.map((project) =>
                  renderSection(
                    project.name,
                    screens.filter((s) => s.projectId === project.id).length,
                    project.id,
                    project.id,
                    project,
                    screens.filter((s) => s.projectId === project.id),
                  ),
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
